import { OptionType } from "components/general/CustomMultiSelect";
import CustomSelect from "components/general/CustomSelect";
import { IOrderDetails } from "interfaces/IOrder"
import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import DateRangeIcon from '@mui/icons-material/DateRange';
import { SingleValue } from "react-select";
import { es } from 'date-fns/locale';
import { IUpdateOrder } from "interfaces/IUpdateOrder";
import { validDate } from "helpers/dates";
import { IValidDate } from "interfaces/IValidDate";
import { format } from "date-fns";

/**
 * custom props for the modal component
 */
interface Props {
    /**
     * The order state
     */
    order: IOrderDetails;
    /**
     * Show state of the modal
     */
    show: boolean;
    /**
     * on hide function for the modal
     */
    onHide: () => void;
    /**
     * call back function to open the confirm modal
     */
    showConfirm: (body: IUpdateOrder) => void;
}

/**
 * The update order modal component
 */
export const UpdateOrderModal = ({ order, show, onHide, showConfirm }: Props) => {
    const options = [
        { value: "Cancelado", label: "Cancelado" },
        { value: "Entregado", label: "Entregado" },
        { value: "En tránsito", label: "En tránsito" },
        { value: "Pendiente", label: "Pendiente" },
        { value: "Preparando", label: "Preparando" },
    ];
    const [courier, setCourier] = useState<string>("");
    const [status, setStatus] = useState<OptionType | null>(null);
    const [trackingNumber, setTrackingNumber] = useState<string>("");
    const [deliveryDate, setDeliveryDate] = useState<string | undefined>();
    const [selectedDate, setSelectedDate] = useState<Date | null>();
    const [estimatedDelivery, setEstimatedDelivery] = useState<string>();
    const datePickerRef = useRef<DatePicker | null>(null);

    /**
     * Handles the courier change input
     */
    const handleCourier = (value: string) => {
        if (value === "") {
            setCourier(order.tracking_name);
            return;
        }
        setCourier(value);
    }

    /**
     * Handles the Tracking Number change input
     */
    const handleTrackingNumber = (value: string) => {
        if (value === "") {
            setTrackingNumber(order.tracking_number);
            return;
        }
        setTrackingNumber(value);
    }

    /**
     * Handles the status select change
     */
    const handleStatus = (option: SingleValue<OptionType>) => setStatus(option);

    /**
    * Opens the status related modal
    */
    const onSaveClick = () => {
        const isStatusChanged = status !== null && status.value !== order.status;
        const isCourierChanged = courier !== "" && courier !== order.tracking_name;
        const isTrackingNumberChanged = trackingNumber !== "" && trackingNumber !== order.tracking_number;

        const isDeliveryDateChanged = isEstimatedDeliveryValid(order.estimated_delivery, estimatedDelivery ?? "");

        const shouldUpdate = isStatusChanged || isCourierChanged || isTrackingNumberChanged || isDeliveryDateChanged.isValid;
        if (shouldUpdate) {
            const updateBody: IUpdateOrder = {
                status: isStatusChanged ? status.value : order.status,
                tracking_name: isCourierChanged ? courier : order.tracking_name,
                tracking_number: isTrackingNumberChanged ? trackingNumber : order.tracking_number.toString(),
                estimated_delivery: (isDeliveryDateChanged.isValid) && status?.value === "En tránsito" 
                    ? isDeliveryDateChanged.formattedDate
                    : order.estimated_delivery
            }
            showConfirm(updateBody);
        }
        handleHide();
    };

    /**
     * Determines whether the date is valid or has changed considering the actual estimated delivery date
     */
    const isEstimatedDeliveryValid = (orderDate: string | undefined, pickedDate: string): IValidDate => {
        const isDatePicked = pickedDate !== undefined; /** Makes sure the date is picked by user */
        if (!isDatePicked)
            return { isValid: false, formattedDate: "" };
        const validPickedDate = validDate(pickedDate, 'yyyy-MM-dd HH:mm:ss'); /**Validates both dates and format them */
        const validOrderDate = validDate(orderDate, 'yyyy-MM-dd HH:mm:ss'); /**Validates both dates and format them */
        if (!validPickedDate.isValid) { /**If the picked date is invalid returns false */
            return { isValid: false, formattedDate: "" };
        }
        const isDateChanged = validOrderDate.formattedDate !== validPickedDate.formattedDate;
        return { isValid: isDateChanged, formattedDate: validPickedDate.formattedDate };
    }

    /**
     * Handles the modal hide to reset values and close modal
     */
    const handleHide = (shouldCloseModal: boolean = true) => {
        setCourier("");
        shouldCloseModal && setStatus(null);
        setTrackingNumber("");
        setEstimatedDelivery(undefined);
        setDeliveryDate(undefined);
        setSelectedDate(null);
        shouldCloseModal && onHide();
    }

    /**
     * Use effect used to update the estimated delivery state
     * it is needed to have it on a separate state because the date and the selected state differ in format
     * [deliveryDate] is the picked date in string format
     */
    useEffect(() => {
        if (!deliveryDate)
            return;
        const localeDate = format(deliveryDate, 'yyyy-MM-dd HH:mm:ss');
        setEstimatedDelivery(localeDate);
    }, [deliveryDate]);

    /**
     * use effect used to clear the form data by changing from certain state
     * [status] The selected status for the order
     */
    useEffect(() => {
        if (status?.value !== "En tránsito") {
            handleHide(false);
        }
    }, [status]);

    /**
     * Use effect used to set the current status and hide certain elements of the modal
     * [show] is the modal state
     */
    useEffect(() => {
        const currentStatus = options.find(option => option.value === order.status);
        setStatus(currentStatus ?? null);
    }, [show]);

    return (
        <Modal show={show} centered className="custom-modal" onHide={handleHide}>
            <Modal.Header className="justify-content-center"><h2><strong>Actualizar estado</strong></h2></Modal.Header>
            <Modal.Body>
                <Form className="d-flex flex-column">
                    <div className="d-flex justify-content-start flex-no-wrap gap-3 align-items-center mb-4">
                        <Form.Label className="align-self-center m-0"><strong>Estado:</strong></Form.Label>
                        <CustomSelect
                            customClass="w-fit-content"
                            onChange={(e) => handleStatus(e)}
                            options={options}
                            value={status}
                            placeholder={status?.label ?? order.status}
                        />
                    </div>
                    {status?.value === "En tránsito" &&
                        <div className="d-flex flex-no-wrap mb-4">
                            <Form.Label className="align-self-center m-0"><strong className="text-nowrap me-2">Servicio de mensajería:</strong></Form.Label>
                            <Form.Control
                                className="align-self-center"
                                style={{ maxHeight: "fit-content" }}
                                size="sm"
                                type="text"
                                onChange={(e) => handleCourier(e.target.value)}
                                value={courier}
                                readOnly={false}
                                placeholder={"Escribe la compañía de servicio"}
                            />
                        </div>
                    }
                    {status?.value === "En tránsito" &&
                        <div className="d-flex flex-no-wrap mb-4">
                            <Form.Label className="align-self-center m-0"><strong className="text-nowrap me-2">Número de guía:</strong></Form.Label>
                            <Form.Control
                                className="align-self-center"
                                style={{ maxHeight: "fit-content" }}
                                size="sm"
                                type="text"
                                onChange={(e) => handleTrackingNumber(e.target.value)}
                                value={trackingNumber}
                                readOnly={false}
                                placeholder={"Número de guía"}
                            />
                        </div>
                    }
                    {status?.label === "En tránsito" &&
                        <div className="d-flex flex-no-wrap mb-4">
                            <Form.Label className="align-self-center m-0"><strong className="text-nowrap me-2">Fecha de entrega:</strong></Form.Label>
                            <DatePicker
                                locale={es}
                                ref={(ref) => (datePickerRef.current = ref)}
                                selected={selectedDate}
                                onChange={(e) => {
                                    setSelectedDate(e);
                                    setDeliveryDate(e?.toString())
                                }}
                                className="form-control date-picker-text-sm"
                                placeholderText="Fecha"
                                dateFormat="dd/MM/yyyy"
                                minDate={new Date()}
                            />
                            <DateRangeIcon
                                className="justify-self-end flex-grow-1 align-self-center"
                                onClick={() => datePickerRef.current?.setOpen(true)}
                                style={{ cursor: "pointer" }}
                            />
                        </div>
                    }
                </Form>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button
                    variant="primary"
                    type="button"
                    onClick={onSaveClick}
                    className=" fw-bold align-item-center text-center me-2"
                    style={{ width: "fit-content" }}
                >
                    Guardar
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    onClick={() => handleHide()}
                    className=" fw-bold align-item-center text-center ms-2"
                    style={{ width: "fit-content" }}
                >
                    Cancelar
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
